import { useCombobox } from 'downshift'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import type { SelectOption } from '@/shared/components/select-field'

export type ComboboxFieldProps = {
  id: string
  label: string
  value?: string
  onValueChange: (value: string | undefined) => void
  inputValue: string
  onInputValueChange: (value: string) => void
  onFocus?: () => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  error?: string
  layout?: 'stacked' | 'inline'
  className?: string
}

const inputClassName =
  'w-full rounded-button-sm border border-foreground/10 bg-surface-muted px-4 py-4 pr-10 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary-focus focus:ring-2 focus:ring-primary-focus/30 disabled:cursor-not-allowed disabled:opacity-50'

const itemClassName =
  'relative flex cursor-pointer select-none items-center rounded-button-sm px-3 py-2 text-sm outline-none'

const isItemSelectionType = (type: string) =>
  type === useCombobox.stateChangeTypes.ItemClick ||
  type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
  type === useCombobox.stateChangeTypes.FunctionSelectItem

export const ComboboxField = ({
  id,
  label,
  value,
  onValueChange,
  inputValue,
  onInputValueChange,
  onFocus,
  options,
  placeholder = 'Search…',
  disabled = false,
  loading = false,
  error,
  layout = 'stacked',
  className,
}: ComboboxFieldProps) => {
  const isInline = layout === 'inline'
  const selectedItem =
    options.find((option) => option.value === value) ?? null

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    getToggleButtonProps,
    openMenu,
  } = useCombobox({
    id,
    items: options,
    itemToString: (item) => item?.label ?? '',
    selectedItem,
    inputValue,
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges

      if (type === useCombobox.stateChangeTypes.InputClick) {
        return {
          ...changes,
          isOpen: true,
        }
      }

      if (type === useCombobox.stateChangeTypes.InputChange) {
        return {
          ...changes,
          isOpen: true,
        }
      }

      if (
        type === useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem
      ) {
        return {
          ...changes,
          inputValue: state.inputValue,
          isOpen: state.isOpen,
        }
      }

      return changes
    },
    onInputValueChange: ({ inputValue: nextInputValue, type }) => {
      if (type === useCombobox.stateChangeTypes.InputChange) {
        onInputValueChange(nextInputValue ?? '')
      }
    },
    onSelectedItemChange: ({ selectedItem: nextSelected, type }) => {
      if (isItemSelectionType(type)) {
        onValueChange(nextSelected?.value)
      }
    },
  })

  const showMenu = isOpen && !disabled
  const showEmptyState = showMenu && !loading && options.length === 0
  const showOptions = showMenu && options.length > 0

  return (
    <div
      className={cn(
        isInline ? 'flex flex-row items-center gap-4' : 'flex flex-col gap-2',
        className
      )}
    >
      <label
        className={cn(
          'text-sm font-semibold text-foreground',
          isInline && 'w-44 shrink-0'
        )}
        {...getLabelProps()}
      >
        {label}
      </label>

      <div className={cn('relative flex min-w-0 flex-col gap-2', isInline && 'flex-1')}>
        <div className="relative">
          <input
            className={inputClassName}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            {...getInputProps({
              onFocus: () => {
                openMenu()
                onFocus?.()
              },
            })}
          />
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2 text-foreground/60 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled}
            aria-label="Toggle options"
            {...getToggleButtonProps()}
          >
            <ChevronDown className="size-4 shrink-0" aria-hidden />
          </button>
        </div>

        <ul
          className={cn(
            'absolute top-full z-60 mt-1 max-h-60 w-full overflow-auto rounded-button-sm border border-foreground/10 bg-background p-1 shadow-md',
            !showMenu && 'invisible'
          )}
          {...getMenuProps()}
        >
          {loading && options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Searching…</li>
          ) : null}

          {showEmptyState ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">No results</li>
          ) : null}

          {showOptions
            ? options.map((option, index) => (
              <li
                key={option.value}
                className={cn(
                  itemClassName,
                  highlightedIndex === index && 'bg-surface-muted',
                  selectedItem?.value === option.value && 'font-semibold'
                )}
                {...getItemProps({ item: option, index })}
              >
                {option.label}
              </li>
            ))
            : null}
        </ul>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  )
}
